import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import SimpleDatePicker from "../../components/SimpleDatePicker";
import { Button, Grid, Box, Typography, CircularProgress, Stack } from "@mui/material";
import { Trophy, Users } from "lucide-react";
import api from "../../services/api";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const hexToRgba = (hex, a = 0.3) => {
  let c = (hex || "").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.substr(0, 2), 16) || 25;
  const g = parseInt(c.substr(2, 2), 16) || 118;
  const b = parseInt(c.substr(4, 2), 16) || 210;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const ChatsUser = () => {
  const themeV4 = useThemeV4();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";

  const axisColor = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,23,42,0.55)";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const emptyBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)";
  const tooltipBg = isDark ? "rgba(20,24,32,0.95)" : "rgba(255,255,255,0.98)";
  const tooltipBorder = isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)";

  const [initialDate, setInitialDate] = useState(new Date());
  const [finalDate, setFinalDate] = useState(new Date());
  const [ticketsData, setTicketsData] = useState({ data: [] });
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    if (companyId) handleGetTicketsInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleGetTicketsInformation = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(
        `/dashboard/ticketsUsers?initialDate=${format(initialDate, "yyyy-MM-dd")}&finalDate=${format(finalDate, "yyyy-MM-dd")}&companyId=${companyId}`
      );
      setTicketsData(data);
    } catch (error) {
      toast.error(i18n.t("dashboard.charts.errorFetchingTickets"));
    } finally {
      setIsLoading(false);
    }
  };

  const items = useMemo(() => Array.isArray(ticketsData?.data) ? ticketsData.data : [], [ticketsData]);

  const totalTickets = useMemo(() => items.reduce((acc, x) => acc + (Number(x.quantidade) || 0), 0), [items]);
  const topUser = useMemo(
    () => items.reduce((mx, x) => ((Number(x.quantidade) || 0) > (mx?.quantidade || 0) ? x : mx), null),
    [items]
  );

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: isDark ? "#e9edef" : "#0f172a",
        bodyColor: isDark ? "#cfd6dc" : "#475569",
        borderColor: tooltipBorder,
        borderWidth: 1,
        boxPadding: 8,
        padding: 12,
        cornerRadius: 10,
        usePointStyle: true,
        displayColors: true,
        titleFont: { weight: 700, size: 12 },
        bodyFont: { size: 13, weight: 600 },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: gridColor, drawBorder: false, drawTicks: false },
        ticks: { color: axisColor, font: { size: 11, weight: 500 }, padding: 8, precision: 0 },
        border: { display: false },
      },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: { color: axisColor, font: { size: 12, weight: 600 } },
        border: { display: false },
      },
    },
  };

  const dataCharts = {
    labels: items.map((item) => item.nome),
    datasets: [
      {
        label: i18n.t("dashboard.charts.ticketsLabel"),
        data: items.map((item) => Number(item.quantidade) || 0),
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return hexToRgba(PRIMARY_MAIN, 0.6);
          const grad = c.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
          grad.addColorStop(0, hexToRgba(PRIMARY_MAIN, 0.85));
          grad.addColorStop(1, hexToRgba(PRIMARY_DARK, 0.85));
          return grad;
        },
        hoverBackgroundColor: PRIMARY_MAIN,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 22,
      },
    ],
  };

  const KpiPill = ({ icon, label, value, accent }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2.5,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        flex: 1,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
          color: "#fff",
          boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={5} md={4}>
          <SimpleDatePicker
            value={initialDate}
            onChange={(newValue) => setInitialDate(newValue)}
            label={i18n.t("dashboard.date.initialDate")}
          />
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <SimpleDatePicker
            value={finalDate}
            onChange={(newValue) => setFinalDate(newValue)}
            label={i18n.t("dashboard.date.finalDate")}
          />
        </Grid>
        <Grid item xs={12} sm={2} md>
          <Button
            onClick={handleGetTicketsInformation}
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
              color: PRIMARY_CONTRAST,
              transition: "all .2s ease-in-out",
              borderRadius: "12px",
              py: 1,
              fontWeight: 700,
              boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                transform: "translateY(-1px)",
                boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
              },
            }}
          >
            {isLoading ? <CircularProgress size={22} color="inherit" /> : i18n.t("dashboard.buttons.filter")}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <KpiPill
            icon={<Users size={18} />}
            label={i18n.t("dashboard.charts.totalAgents") || "Atendentes"}
            value={items.length}
            accent={PRIMARY_MAIN}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KpiPill
            icon={<Trophy size={18} />}
            label={topUser ? topUser.nome : (i18n.t("dashboard.charts.topAgent") || "Destaque")}
            value={topUser ? `${topUser.quantidade} ${i18n.t("dashboard.charts.ticketsLabel") || "tickets"}` : "—"}
            accent="#F7C82C"
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 3,
          background: isDark
            ? `linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)`
            : `linear-gradient(180deg, ${hexToRgba(PRIMARY_MAIN, 0.025)} 0%, transparent 100%)`,
          border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.05)",
        }}
      >
        <Box sx={{ height: { xs: 280, sm: Math.max(280, items.length * 36 + 60) }, position: "relative" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress sx={{ color: PRIMARY_MAIN }} />
            </Box>
          ) : items.length > 0 ? (
            <Bar options={options} data={dataCharts} />
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: "100%", bgcolor: emptyBg, borderRadius: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: hexToRgba(PRIMARY_MAIN, isDark ? 0.15 : 0.10),
                  color: PRIMARY_MAIN,
                }}
              >
                <Users size={28} />
              </Box>
              <Typography sx={{ fontSize: 13, color: "text.secondary", textAlign: "center", maxWidth: 320 }}>
                {i18n.t("dashboard.charts.noDataAvailable")}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};
